import React, { useState, useCallback, useRef, useEffect } from 'react';
import { motion } from 'framer-motion';
import { FormulaEngine } from './FormulaEngine';

interface Cell {
  value: string;
  formula?: string;
  type?: 'text' | 'number' | 'formula';
  style?: {
    backgroundColor?: string;
    color?: string;
    fontWeight?: string;
    textAlign?: string;
  };
}

interface ResizeState {
  columnWidths: Record<number, number>;
  rowHeights: Record<number, number>;
}

interface SpreadsheetGridProps {
  data: any[];
  columns: any[];
  onCellChange: (rowIndex: number, colIndex: number, value: string) => void;
  onCellSelect: (rowIndex: number, colIndex: number) => void;
  selectedCell: { row: number; col: number } | null;
  formulaEngine: FormulaEngine;
  onResize?: (resizeState: ResizeState) => void;
  className?: string;
}

export const SpreadsheetGrid: React.FC<SpreadsheetGridProps> = ({
  data,
  columns,
  onCellChange,
  onCellSelect,
  selectedCell,
  formulaEngine,
  onResize,
  className = ''
}) => {
  const [editingCell, setEditingCell] = useState<{ row: number; col: number } | null>(null);
  const [editValue, setEditValue] = useState('');
  const [resizeState, setResizeState] = useState<ResizeState>({
    columnWidths: {},
    rowHeights: {}
  });
  const [isResizing, setIsResizing] = useState<{
    type: 'column' | 'row';
    index: number;
    startPos: number;
    startSize: number;
  } | null>(null);
  const gridRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);

  const rows = Math.max(data.length, 20);
  const cols = Math.max(columns.length, 10);

  const getColumnWidth = useCallback((colIndex: number): number => {
    return resizeState.columnWidths[colIndex] || 120;
  }, [resizeState.columnWidths]);

  const getRowHeight = useCallback((rowIndex: number): number => {
    return resizeState.rowHeights[rowIndex] || 32;
  }, [resizeState.rowHeights]);

  const handleMouseDown = useCallback((e: React.MouseEvent, type: 'column' | 'row', index: number) => {
    e.preventDefault();
    const startPos = type === 'column' ? e.clientX : e.clientY;
    const currentSize = type === 'column' 
      ? getColumnWidth(index)
      : getRowHeight(index);
    
    setIsResizing({ type, index, startPos, startSize: currentSize });
  }, [getColumnWidth, getRowHeight]);

  const handleMouseMove = useCallback((e: MouseEvent) => {
    if (!isResizing) return;

    const currentPos = isResizing.type === 'column' ? e.clientX : e.clientY;
    const delta = currentPos - isResizing.startPos;
    const newSize = Math.max(50, isResizing.startSize + delta);

    setResizeState(prev => {
      const newState = {
        ...prev,
        [isResizing.type === 'column' ? 'columnWidths' : 'rowHeights']: {
          ...prev[isResizing.type === 'column' ? 'columnWidths' : 'rowHeights'],
          [isResizing.index]: newSize
        }
      };
      
      if (onResize) {
        onResize(newState);
      }
      
      return newState;
    });
  }, [isResizing, onResize]);

  const handleMouseUp = useCallback(() => {
    setIsResizing(null);
  }, []);

  useEffect(() => {
    if (isResizing) {
      document.addEventListener('mousemove', handleMouseMove);
      document.addEventListener('mouseup', handleMouseUp);
      
      return () => {
        document.removeEventListener('mousemove', handleMouseMove);
        document.removeEventListener('mouseup', handleMouseUp);
      };
    }
  }, [isResizing, handleMouseMove, handleMouseUp]);

  const getCellValue = useCallback((rowIndex: number, colIndex: number): string => {
    if (rowIndex < data.length && colIndex < columns.length) {
      const cellData = data[rowIndex];
      const columnKey = columns[colIndex]?.key || columns[colIndex]?.id;
      return cellData[columnKey] || '';
    }
    return '';
  }, [data, columns]);

  const getCellAddress = useCallback((row: number, col: number): string => {
    const colLetter = String.fromCharCode(65 + col);
    return `${colLetter}${row + 1}`;
  }, []);

  const handleCellClick = useCallback((rowIndex: number, colIndex: number) => {
    onCellSelect(rowIndex, colIndex);
  }, [onCellSelect]);

  const handleCellDoubleClick = useCallback((rowIndex: number, colIndex: number) => {
    const currentValue = getCellValue(rowIndex, colIndex);
    setEditingCell({ row: rowIndex, col: colIndex });
    setEditValue(currentValue);
  }, [getCellValue]);

  const handleEditKeyDown = useCallback((e: React.KeyboardEvent) => {
    if (e.key === 'Enter') {
      if (editingCell) {
        let processedValue = editValue;
        
        if (editValue.startsWith('=')) {
          try {
            const result = formulaEngine.evaluate(editValue, { 
              data, 
              currentRow: editingCell.row, 
              currentCol: editingCell.col.toString() 
            });
            processedValue = result.toString();
          } catch (error) {
            processedValue = '#ERROR';
          }
        }
        
        onCellChange(editingCell.row, editingCell.col, processedValue);
        setEditingCell(null);
        setEditValue('');
      }
    } else if (e.key === 'Escape') {
      setEditingCell(null);
      setEditValue('');
    }
  }, [editingCell, editValue, formulaEngine, data, onCellChange]);

  useEffect(() => {
    if (editingCell && inputRef.current) {
      inputRef.current.focus();
      inputRef.current.select();
    }
  }, [editingCell]);

  const renderColumnHeaders = () => (
    <div className="flex bg-gray-800 border-b border-gray-600">
      <div className="w-12 h-8 border-r border-gray-600 flex items-center justify-center text-sm text-gray-400 shadow-sm">
        
      </div>
      {Array.from({ length: cols }, (_, colIndex) => (
        <div
          key={colIndex}
          className="border-r border-slate-600 flex items-center justify-center text-sm font-medium text-slate-300 bg-slate-800 relative shadow-sm"
          style={{ width: getColumnWidth(colIndex), height: 32 }}
        >
          {String.fromCharCode(65 + colIndex)}
          <div
            className="absolute right-0 top-0 w-1 h-full cursor-col-resize hover:bg-[#4267A1]/50 opacity-0 hover:opacity-100 transition-opacity"
            onMouseDown={(e) => handleMouseDown(e, 'column', colIndex)}
          />
        </div>
      ))}
    </div>
  );

  const renderRowHeader = (rowIndex: number) => (
    <div 
      className="w-12 border-r border-gray-600 flex items-center justify-center text-sm text-gray-400 bg-gray-800 relative shadow-sm"
      style={{ height: getRowHeight(rowIndex) }}
    >
      {rowIndex + 1}
      <div
        className="absolute bottom-0 left-0 w-full h-1 cursor-row-resize hover:bg-[#4267A1]/50 opacity-0 hover:opacity-100 transition-opacity"
        onMouseDown={(e) => handleMouseDown(e, 'row', rowIndex)}
      />
    </div>
  );

  const renderCell = (rowIndex: number, colIndex: number) => {
    const isSelected = selectedCell?.row === rowIndex && selectedCell?.col === colIndex;
    const isEditing = editingCell?.row === rowIndex && editingCell?.col === colIndex;
    const cellValue = getCellValue(rowIndex, colIndex);

    return (
      <div
        key={`${rowIndex}-${colIndex}`}
        className={`
          border-r border-b border-gray-600 relative cursor-cell shadow-sm
          ${isSelected ? 'ring-2 ring-[#4267A1] bg-[#4267A1]/20 shadow-md' : 'hover:bg-gray-700/50 hover:shadow-md'}
          ${isEditing ? 'bg-slate-800 shadow-lg' : ''}
        `}
        style={{ 
          width: getColumnWidth(colIndex), 
          height: getRowHeight(rowIndex) 
        }}
        onClick={() => handleCellClick(rowIndex, colIndex)}
        onDoubleClick={() => handleCellDoubleClick(rowIndex, colIndex)}
      >
        {isEditing ? (
          <input
            ref={inputRef}
            type="text"
            value={editValue}
            onChange={(e) => setEditValue(e.target.value)}
            onKeyDown={handleEditKeyDown}
            onBlur={() => {
              setEditingCell(null);
              setEditValue('');
            }}
            className="w-full h-full px-1 text-sm bg-slate-800 text-white border-none outline-none"
          />
        ) : (
          <div className="w-full h-full px-1 flex items-center text-sm text-gray-200 truncate">
            {cellValue}
          </div>
        )}
      </div>
    );
  };

  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      className={`spreadsheet-grid ${className}`}
    >
      <div ref={gridRef} className="overflow-auto max-h-96 border border-gray-600 mx-2 shadow-lg">
        {renderColumnHeaders()}
        {Array.from({ length: rows }, (_, rowIndex) => (
          <div key={rowIndex} className="flex">
            {renderRowHeader(rowIndex)}
            {Array.from({ length: cols }, (_, colIndex) => renderCell(rowIndex, colIndex))}
          </div>
        ))}
      </div>
    </motion.div>
  );
};
